import { Checkbox, Input, Select, notification } from 'antd';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Dropdown } from 'ui';
import { transformUrlParamsToSearchFilters } from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { dropdownWithOperatorFilterIds, searchOperators, urlSigns } from '../../utils/app-configs';
import { determineOperatorAndValue } from '../../utils/helpers';

const Buttons = styled.div`
  text-align: right;
  > button {
    margin-right: 5px;
    text-transform: uppercase;
  }
`;

const CheckboxDescription = styled.span`
  font-size: 16px;
  margin-left: 5px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  row-gap: 20px;
`;

const StyledInput = styled(Input)`
  width: 100% !important;
  height: 32px !important;
  border-radius: 6px !important;
  cursor: text;
`;

const StyledSelect = styled(Select)`
  height: 32px !important;
  width: 15%;
  font-size: 14px !important;
  .ant-select-selector {
    font-size: 14px !important;
    border-radius: 6px !important;
  }
`;

const InputWrapper = styled.div`
  width: 100% !important;
`;

const StyledCheckboxGroup = styled(Checkbox.Group)`
  flex-flow: row wrap;
  justify-content: flex-start;
  label {
    margin-left: 0px !important;
    margin-right: 8px !important;
    font-size: 14px !important;
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  column-gap: 20px;
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;
  font-size: 14px !important;
  .ant-select-selector {
    font-size: 14px !important;
    border-radius: 6px !important;
  }
`;

const InputFlex = styled.div`
  display: flex;
  flex-flow: column nowrap;
  row-gap: 20px;
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 28px;
  width: 11%;
`;

const InputDiv = styled.div`
  width: 89%;
  display: flex;
  align-items: center
`;

const InputRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
`;

const SearchFilters = ({
  filters,
  searchValue,
  history,
  closeFilters,
  searchType,
  refetch,
  clear,
}) => {
  const intl = useIntl();
  const keywordRef = useRef();
  const [defaultValues, setDefaultValues] = useState({});
  const [visible, setVisible] = useState(true);
  const [values, setValues] = useState({});

  useEffect(() => () => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (visible && keywordRef) {
      keywordRef.current?.focus();
    }
  }, [visible, keywordRef]);

  const getDefaultFilters = () => {
    if (filters?.length) {
      return filters.reduce((tot, curr) => {
        const value = curr.type === 'bool' ? curr.defaultChecked : '';
        if (curr.type === 'bool') {
          return {
            ...tot,
            [curr.id]: {
              prefix: curr?.prefix,
              value: value || false,
              operator: null,
              shortHand: curr?.shortHand,
              valueMapping: curr?.valueMapping,
            },
          };
        }
        if (curr.type === 'select' || curr.type === 'dropdown') {
          if (dropdownWithOperatorFilterIds.includes(curr?.id)) {
            const matchAnycurrentSearchOperators = curr?.operators?.find(o =>
              o.id === searchOperators?.matchAny.id);
            return {
              ...tot,
              [curr.id]: {
                prefix: curr?.prefix,
                value,
                operator: matchAnycurrentSearchOperators?.id,
                shortHand: matchAnycurrentSearchOperators?.shortHand,
                valueMapping: curr?.valueMapping,
              },
            };
          }
          return {
            ...tot,
            [curr.id]: {
              prefix: curr?.prefix,
              value,
              operator: null,
              shortHand: curr?.shortHand,
              valueMapping: curr?.valueMapping,
            },
          };
        }
        const containscurrentSearchOperators = curr?.operators?.find(o =>
          o.id === searchOperators?.contains.id,
        );
        return {
          ...tot,
          [curr.id]: {
            prefix: curr?.prefix,
            value,
            operator: containscurrentSearchOperators?.id,
            shortHand: containscurrentSearchOperators?.shortHand,
            valueMapping: curr?.valueMapping,
          },
        };
      }, {});
    }
    return {};
  };

  const fillFields = (fields) => {
    const params = qs.parse(history.location.search);
    if (params.searchFilters) {
      const filterArray = transformUrlParamsToSearchFilters(
        decodeURIComponent(params.searchFilters),
      );
      const result = Object.keys(fields).reduce((tot, f) => {
        let fieldValue = fields[f].value;
        let fieldOperator = fields[f]?.operator || null;
        let valueText = null;
        if (filterArray && Object.keys(filterArray)?.find(k => k === f)) {
          const filterValue = filterArray[f];
          const [, s] = filterValue.split(':');
          const { value, operator } = determineOperatorAndValue(s);
          fieldValue = value;
          fieldOperator = operator;
        }
        if (f === 'status' && fields[f].valueMapping && fieldValue) {
          valueText = fieldValue;
          fieldValue = fieldValue
            .split(';')
            ?.map(v => Object.entries(fields[f].valueMapping)?.find(([, value]) => value === v)[0])
            .join(';');
        }
        if (dropdownWithOperatorFilterIds.includes(f) && fieldValue) {
          valueText = fieldValue;
          fieldValue = fieldValue
            .split(';');
        }

        return {
          ...tot,
          [f]: {
            ...fields[f],
            value: fieldValue,
            operator: fieldOperator,
            shortHand: fieldOperator ?
              searchOperators[fieldOperator].shortHand :
              fields[f].shortHand,
            valueText,
          },
        };
      }, {});
      return {
        ...result,
        keyword: {
          ...fields.keyword,
          value: searchValue,
        },
      };
    }
    return {
      ...fields,
      keyword: {
        ...fields.keyword,
        value: searchValue,
      },
    };
  };

  useEffect(() => {
    if (filters?.length) {
      const defaults = getDefaultFilters();
      setDefaultValues(defaults);
      setValues(defaults);
    }
  }, [filters]);

  useEffect(() => {
    if (visible && defaultValues && Object.keys(defaultValues).length) {
      setValues(fillFields(defaultValues));
    }
  }, [visible, defaultValues]);

  const handleInputChange = (id, value) => {
    setValues(v => ({
      ...v,
      [id]: {
        ...v[id],
        value,
      },
    }));
  };

  const handleOperatorChange = (id, operator, shortHand) => {
    setValues(v => ({
      ...v,
      [id]: {
        ...v[id],
        operator,
        shortHand,
      },
    }));
  };

  const handleCheckBoxChange = (id, value, shortHand, valueMapping) => {
    let valueText = '';
    if (valueMapping) {
      valueText = valueMapping[value.toString()];
    }
    setValues(v => ({
      ...v,
      [id]: {
        ...v[id],
        value,
        shortHand,
        valueText,
      },
    }));
  };

  const handleDropdownChange = (id, value) => {
    if (dropdownWithOperatorFilterIds.includes(id)) {
      const valueText = value.toString().split(',').join(';');
      setValues(v => ({
        ...v,
        [id]: {
          ...v[id],
          value,
          valueText,
        },
      }));
    } else {
      setValues(v => ({
        ...v,
        [id]: {
          ...v[id],
          value,
        },
      }));
    }
  };

  const handleSelectChange = (id, value, valueMapping) => {
    let valueText = '';
    if (valueMapping) {
      valueText = value?.map(v => valueMapping[v])?.join(';');
    }
    setValues(v => ({
      ...v,
      [id]: {
        ...v[id],
        value: value.join(';'),
        valueText,
      },
    }));
  };

  const getParamValue = (prefix, value, shortHand) => {
    const encodedValue = encodeURIComponent(value);
    if (shortHand) {
      return `${prefix}:${shortHand.replace(/{value}/, encodedValue)}`;
    }
    return `${prefix}:${encodedValue}`;
  };

  const handleSearch = () => {
    const currentPath = decodeURIComponent(history.location.pathname);
    const keywords = values.keyword.value;
    const queryString = Object.entries(values).reduce((tot, [key, value]) => {
      if (value.value && dropdownWithOperatorFilterIds.includes(key) && !value.value.length) {
        return tot;
      }
      if (value.value && key !== 'keyword') {
        const urlString = getParamValue(
          value.prefix,
          value.valueText || value.value,
          value.shortHand);
        return tot ?
          `${tot}${urlSigns.filterSeparator}${key}${urlSigns.keyValueSeparator}${urlString}` :
          `${key}${urlSigns.keyValueSeparator}${urlString}`;
      }
      return tot;
    }, '');

    closeFilters();
    if (!queryString && !keywords) {
      clear();
    } else if (keywords === '\\') {
      notification.error({
        message: intl.formatMessage({ id: 'search.SEARCH_ERROR' }),
        key: 'specialCharacterError',
        placement: 'top',
      });
      clear();
    } else if (history.location.pathname.includes('/relations')) {
      // eslint-disable-next-line max-len
      history.replace(`/search/${encodeURIComponent(keywords || '*')}${searchType && searchType !== 'all' ?
        `/type/${searchType}` :
        ''
        }`,
        {
          searchFilters: queryString ? encodeURIComponent(queryString) : '',
        });
      refetch();
    } else {
      history.replace(history.location.pathname.includes('/search') ?
        currentPath
          .replace(
            // eslint-disable-next-line no-useless-escape
            /\/search\/[^\/]*(\/)?/g,
            `/search/${encodeURIComponent(keywords || '*')}$1`,
          )
          // eslint-disable-next-line no-useless-escape
          .replace(/\/page\/[0-9]+/g, '') :
        `/search/${encodeURIComponent(keywords || '*')}${searchType && searchType !== 'all' ?
          `/type/${searchType}` :
          ''
        }`,
        {
          searchFilters: queryString ? encodeURIComponent(queryString) : '',
        });
      refetch();
    }
  };

  const renderOperatorsDropdown = (id, operators) => {
    if (operators?.length) {
      return <StyledSelect
        options={operators?.map(o => ({
          value: o.id,
          label: o.description,
          shortHand: o.shortHand,
        }))}
        value={values[id]?.operator}
        onSelect={(e, o) => handleOperatorChange(id, o.value, o.shortHand)}
      />;
    }
    return null;
  };

  const renderCheckbox = (id, description, shortHand, valueMapping) => <InputWrapper>
    <Checkbox
      checked={values[id]?.value}
      onChange={v => handleCheckBoxChange(id, v.target.checked, shortHand, valueMapping)}
      data-test-id={`ant-checkbox-${id}`}
    />
    <CheckboxDescription>{description}</CheckboxDescription>
  </InputWrapper>;

  const renderInput = (id, operators = [], isKeyWord) => <InputWrapper>
    <SelectWrapper>
      {renderOperatorsDropdown(id, operators)}
      <StyledInput
        className='filter-input'
        ref={isKeyWord ? keywordRef : null}
        value={values[id]?.value}
        onChange={v => handleInputChange(id, v.target.value)}
        data-test-id={`ant-input-${id}`}
      />
    </SelectWrapper>
  </InputWrapper>;

  const renderDropdown = (id, options = [], multiSelect = false, operators = []) => <InputWrapper>
    <SelectWrapper>
      {renderOperatorsDropdown(id, operators)}
      <StyledDropdown
        multiSelect={multiSelect}
        values={options}
        disabled={!options || !Object.keys(options).length}
        data-test-id={`ant-dropdown-${id}`}
        // We need to apply a undefined here to make sure we don't create a empty tag or
        // show selected values. We can't combine this because this will default on
        // values[id]?.value, this is null = so a empty tag
        value={dropdownWithOperatorFilterIds.includes(id) ?
          // eslint-disable-next-line no-undefined
          values[id]?.value || undefined :
          values[id]?.value}
        onChange={e => handleDropdownChange(id, e)}
        valueKey='id'
        valueLabel='description'
        allowClear={true}
        optionLabelProp={dropdownWithOperatorFilterIds.includes(id) ? 'id' : 'children'}
      />
    </SelectWrapper>
  </InputWrapper>;

  const renderSelect = (id, options = [], valueMapping) => <InputWrapper>
    <StyledCheckboxGroup
      options={options?.map(o => ({
        label: o.description,
        value: o.id,
      }))}
      onChange={e => handleSelectChange(id, e, valueMapping)}
      value={values[id]?.value?.split(';')}
      onClick={e => e.target.focus()}
      data-test-id={`ant-select-${id}`}
    />
  </InputWrapper>;

  const renderField = (f) => {
    if (f.type === 'bool') {
      return renderCheckbox(f.id, f.description, f.shortHand, f.valueMapping);
    }
    if (f.type === 'keyword' || f.type === 'text') {
      return renderInput(f.id, f.operators, f.type === 'keyword');
    }
    if (f.type === 'dropdown') {
      return renderDropdown(f.id, f.searchFilterOptions, f?.allowMultipleValues, f?.operators);
    }
    if (f.type === 'select') {
      return renderSelect(f.id, f.searchFilterOptions, f.valueMapping);
    }
    return null;
  };

  return (
    <Wrapper>
      <InputFlex>
        {filters?.filter(f => f.type !== 'dropdown' || f.searchFilterOptions?.length !== 0)
          .map(f =>
            <InputRow key={f.id}>
              {f.type !== 'bool' ?
                <Label>{f.description}</Label> :
                null
              }
              <InputDiv>
                {renderField(f)}
              </InputDiv>
            </InputRow>,
          )}

      </InputFlex>
      <Buttons>
        <Button
          onClick={() => setValues(defaultValues)}
          disabled={false}
        >
          <FormattedMessage id='search.CLEAR_FILTERS' />
        </Button>
        <Button
          type='secondary'
          onClick={handleSearch}
          loading={false}
          disabled={false}
        >
          <FormattedMessage id='search.SEARCH' />
        </Button>
      </Buttons>
    </Wrapper>
  );
};

SearchFilters.propTypes = {
  filters: PropTypes.array,
  searchValue: PropTypes.string,
  history: PropTypes.object,
  closeFilters: PropTypes.func,
  searchType: PropTypes.string,
  refetch: PropTypes.func,
  clear: PropTypes.func,
};

export default withRouter(SearchFilters);
